import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Store, ArrowRight, Loader2, Mail, Lock, Eye, EyeOff, User, Check, Crown, Star, AlertTriangle, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { vendorCategoryNames } from '@/lib/vendorCategories';
import { fetchVendorSelectableCategories } from '@/lib/vendorCategorySource';
import { getAuthErrorMessage, normalizeEmail } from '@/lib/authErrorMessage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const isRetryableNetworkError = (error) => !error?.response && (error?.code === 'ECONNABORTED' || error?.message === 'Network Error');

// Detect in-app browsers (Instagram, Facebook, etc.)
const isInAppBrowser = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  return /FBAN|FBAV|Instagram|Line\/|Snapchat|Twitter/i.test(ua);
};

const PLANS = {
  'Standard Monthly': { price: '£10', period: '/month', featured: false, description: 'Perfect for getting started' },
  'Annual': { price: '£100', period: '/year', featured: false, description: '2 months FREE!', savings: 'Save £20', popular: true },
  'Featured Monthly': { price: '£20', period: '/month', featured: true, description: 'Maximum visibility' },
  'Featured Annual': { price: '£200', period: '/year', featured: true, description: '2 months FREE!', savings: 'Save £40' }
};

export default function VendorAuthPage() {
  const [searchParams] = useSearchParams();
  const initialMode = searchParams.get('mode') || 'login';
  const [mode, setMode] = useState(initialMode); // 'login' or 'register'
  const [showInAppWarning, setShowInAppWarning] = useState(false);
  
  useEffect(() => {
    setShowInAppWarning(isInAppBrowser());
  }, []);
  
  return (
    <div className="min-h-screen py-12" data-testid="vendor-auth-page">
      {/* In-App Browser Warning */}
      {showInAppWarning && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white px-4 py-3 z-50">
          <div className="container-wedding flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">You're using Instagram/Facebook browser</p>
              <p className="text-sm opacity-90">For the best experience, please open this page in Safari (iPhone) or Chrome (Android).</p>
              <button 
                onClick={() => {
                  // Try to open in external browser
                  const url = window.location.href;
                  window.open(url, '_blank');
                }}
                className="mt-2 inline-flex items-center gap-1 bg-white text-amber-600 px-3 py-1 rounded text-sm font-medium"
              >
                <ExternalLink className="h-3 w-3" />
                Open in Browser
              </button>
            </div>
            <button onClick={() => setShowInAppWarning(false)} className="text-white/80 hover:text-white">
              ✕
            </button>
          </div>
        </div>
      )}
      
      <div className={`container-wedding ${showInAppWarning ? 'pt-24' : ''}`}>
        {/* Logo */}
        <div className="text-center mb-6">
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_17b3eba4-e170-4c07-a419-15dd5729217a/artifacts/ndr9m4c4_logo%20blk%2026%20cropped.png"
              alt="Gibraltar Wedding Expo"
              className="h-16 mx-auto"
            />
          </Link>
        </div>
        
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Store className="h-8 w-8 text-[#D4AF37]" />
          </div>
          <h1 className="heading-lg mb-2">Vendor Portal</h1>
          <p className="text-stone-600">Grow your wedding business in Gibraltar</p>
        </div>

        <div className="max-w-3xl mx-auto mb-8 rounded-[24px] border border-[#D4AF37]/30 bg-white/90 p-5 shadow-sm" data-testid="vendor-auth-account-note">
          <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#b8911e] mb-2">Vendor access only</p>
              <p className="text-sm text-stone-700">Use this portal only if you are a wedding business. Public listings stay hidden until the admin switches them on after payment is received.</p>
            </div>
            <Link to="/client/auth" data-testid="vendor-auth-client-portal-link">
              <Button className="w-full border border-[#5C0120] bg-[#5C0120] text-white hover:bg-[#5C0120]/90 md:w-auto">
                I&apos;m planning a wedding — take me to Couples Sign in
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Mode Toggle */}
        <div className="flex justify-center gap-2 mb-8 max-w-md mx-auto">
          <button
            onClick={() => setMode('login')}
            className={`flex-1 py-3 text-sm font-medium rounded-sm transition-colors ${
              mode === 'login' ? 'bg-[#D4AF37] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            data-testid="vendor-login-tab"
          >
            Sign In
          </button>
          <button
            onClick={() => setMode('register')}
            className={`flex-1 py-3 text-sm font-medium rounded-sm transition-colors ${
              mode === 'register' ? 'bg-[#D4AF37] text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
            data-testid="vendor-register-tab"
          >
            Apply Now
          </button>
        </div>
        
        {mode === 'login' ? (
          <VendorLoginForm onSwitchToRegister={() => setMode('register')} />
        ) : (
          <VendorRegisterForm onSwitchToLogin={() => setMode('login')} />
        )}
        
        {/* Client CTA */}
        <div className="mt-8 max-w-md mx-auto p-6 bg-[#5C0120]/5 border border-[#5C0120]/20 rounded-sm text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <User className="h-5 w-5 text-[#5C0120]" />
            <span className="font-semibold text-stone-800">Planning a wedding?</span>
          </div>
          <p className="text-sm text-stone-600 mb-4">
            Sign up as a couple to save favourite vendors and plan your day.
          </p>
          <Link to="/client/auth">
            <Button variant="outline" className="border-[#5C0120] text-[#5C0120] hover:bg-[#5C0120]/10" data-testid="vendor-auth-client-cta-button">
              Go to Couples / Guests Portal
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function VendorLoginForm({ onSwitchToRegister }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');
    
    try {
      let response;
      const payload = { ...formData, email: normalizeEmail(formData.email) };
      const config = { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

      try {
        response = await axios.post(`${API}/vendor/login`, payload, config);
      } catch (error) {
        if (!isRetryableNetworkError(error)) {
          throw error;
        }
        response = await axios.post(`${API}/vendor/login`, payload, config);
      }
      localStorage.setItem('vendorToken', response.data.token);
      localStorage.setItem('vendorData', JSON.stringify(response.data.vendor));
      localStorage.setItem('vendorLastActivity', Date.now().toString());
      window.dispatchEvent(new Event('vendor-session-changed'));
      if (response.data?.vendor?.force_password_change) {
        toast.info('Your password was reset by admin. Please change it now.');
        navigate('/vendor/dashboard', { state: { forcePasswordChange: true } });
      } else {
        navigate('/vendor/dashboard');
      }
    } catch (err) {
      console.error('Vendor login error:', err);
      setError(getAuthErrorMessage(err, 'Sign in failed. Please check your details and try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;

    setForgotLoading(true);
    try {
      const response = await axios.post(`${API}/vendor/forgot-password`, { email: forgotEmail });
      if (response.data?.delivery_method === 'manual') {
        toast.info('Password reset email delivery is currently unavailable. Please contact the site admin for help resetting your password.');
      } else {
        toast.success('If an account exists with this email, you will receive a password reset link.');
      }
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white p-8 rounded-sm border border-stone-200 shadow-sm" data-testid="vendor-forgot-password-card">
          <button
            onClick={() => setShowForgotPassword(false)}
            className="text-sm text-stone-500 hover:text-stone-700 mb-4"
            data-testid="vendor-forgot-password-back-button"
          >
            ← Back to login
          </button>

          <h2 className="text-xl font-semibold mb-2">Forgot Password</h2>
          <p className="text-stone-600 text-sm mb-6">
            Enter your business email and we&apos;ll send you a password reset link.
          </p>

          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div>
              <Label htmlFor="vendor-forgot-email">Business Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  id="vendor-forgot-email"
                  type="email"
                  placeholder="your@business.com"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  className="pl-10"
                  required
                  data-testid="vendor-forgot-password-email-input"
                />
              </div>
            </div>

            <div className="rounded-sm border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="vendor-forgot-password-note">
              Reset emails depend on the site email setup. If no email arrives, please contact the site admin.
            </div>

            <Button
              type="submit"
              className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white"
              disabled={forgotLoading}
              data-testid="vendor-forgot-password-submit"
            >
              {forgotLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Send Reset Link
            </Button>
          </form>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white p-8 rounded-sm border border-stone-200 shadow-sm">
        <h2 className="text-xl font-semibold text-center mb-6">Sign In to Your Dashboard</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="vendor-email">Business Email</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                id="vendor-email"
                type="email"
                placeholder="your@business.com"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="pl-10"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                data-testid="vendor-email-input"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="vendor-password">Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                id="vendor-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="pl-10 pr-10"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
                data-testid="vendor-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-stone-500 shadow-sm hover:text-stone-700"
                data-testid="vendor-toggle-password"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="text-right">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-sm text-[#D4AF37] font-medium hover:underline"
              data-testid="vendor-forgot-password-trigger"
            >
              Forgot password?
            </button>
          </div>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-3">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
          
          <Button
            type="submit"
            className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white"
            disabled={loading}
            data-testid="vendor-login-btn"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Sign In
          </Button>
          
          <p className="text-center text-sm text-stone-500">
            Don't have an account?{' '}
            <button type="button" onClick={onSwitchToRegister} className="text-[#D4AF37] font-medium hover:underline">
              Apply now
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}

function VendorRegisterForm({ onSwitchToLogin }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [availableCategories, setAvailableCategories] = useState(vendorCategoryNames);
  const [searchParams] = useSearchParams();
  const preSelectedPlan = searchParams.get('plan');
  const [selectedPlan, setSelectedPlan] = useState(preSelectedPlan || '');

  useEffect(() => {
    let isMounted = true;

    const loadCategories = async () => {
      const categories = await fetchVendorSelectableCategories();
      if (isMounted) {
        setAvailableCategories(categories);
      }
    };

    loadCategories();

    return () => {
      isMounted = false;
    };
  }, []);
  
  const [formData, setFormData] = useState({
    business_name: '',
    email: '',
    password: '',
    phone: '',
    primary_category: '',
    secondary_categories: [],
    description: '',
    address: '',
    website: '',
    instagram: '',
    facebook: '',
    payment_method: '',
    invoice_name: ''
  });

  const setPrimaryCategory = (category) => {
    setFormData((prev) => ({
      ...prev,
      primary_category: category,
      secondary_categories: prev.secondary_categories.filter((selected) => selected !== category),
    }));
  };

  const toggleSecondaryCategory = (category) => {
    if (category === formData.primary_category) return;

    if (formData.secondary_categories.includes(category)) {
      setFormData({
        ...formData,
        secondary_categories: formData.secondary_categories.filter((selected) => selected !== category),
      });
    } else {
      setFormData({
        ...formData,
        secondary_categories: [...formData.secondary_categories, category],
      });
    }
  };

  const normaliseOptionalUrl = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    return `https://${trimmed}`;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPlan) {
      setError('Please select a pricing plan');
      return;
    }
    
    if (!formData.primary_category) {
      setError('Please select a primary category');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address (e.g., name@example.com)');
      return;
    }
    
    // Clean up website field - if just "https://" or empty protocol, set to empty
    const cleanedFormData = {
      business_name: formData.business_name.trim(),
      email: normalizeEmail(formData.email),
      password: formData.password,
      phone: formData.phone.trim(),
      primary_category: formData.primary_category,
      secondary_categories: formData.secondary_categories,
      categories: [formData.primary_category, ...formData.secondary_categories],
      description: formData.description.trim(),
      address: formData.address.trim(),
      website: normaliseOptionalUrl(formData.website),
      instagram: formData.instagram.trim(),
      facebook: formData.facebook.trim(),
      payment_method: formData.payment_method,
      invoice_name: formData.invoice_name.trim(),
    };
    
    setLoading(true);
    setError('');
    
    try {
      const payload = {
        ...cleanedFormData,
        pricing_plan: selectedPlan
      };
      const config = { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };

      try {
        await axios.post(`${API}/vendor/register`, payload, config);
      } catch (error) {
        if (!isRetryableNetworkError(error)) {
          throw error;
        }
        await axios.post(`${API}/vendor/register`, payload, config);
      }
      setSuccess(true);
    } catch (err) {
      // Handle validation errors from Pydantic
      const errorDetail = err.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        // Pydantic validation errors come as array
        const messages = errorDetail.map(e => {
          if (e.loc && e.loc.includes('email')) {
            return 'Please enter a valid email address (e.g., name@example.com)';
          }
          if (e.loc && e.loc.includes('website')) {
            return 'Please enter a valid website URL or leave it empty';
          }
          return e.msg || 'Invalid input';
        });
        setError(messages.join('. '));
      } else if (typeof errorDetail === 'string') {
        setError(errorDetail);
      } else {
        setError(getAuthErrorMessage(err, 'Unable to submit right now. Please check the form and try again.'));
      }
    } finally {
      setLoading(false);
    }
  };
  
  if (success) {
    return (
      <div className="max-w-md mx-auto bg-white p-8 rounded-sm border border-stone-200 shadow-sm text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="h-8 w-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold mb-2">Application Submitted!</h3>
        <p className="text-stone-600 mb-4">
          Thank you for your interest in joining Gibraltar Wedding Expo. We will review your application and contact you soon with payment details.
        </p>
        <Button onClick={onSwitchToLogin} className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white">
          Back to Login
        </Button>
      </div>
    );
  }
  
  return (
    <div className="max-w-5xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Pricing Plans */}
        <div>
          <h2 className="text-xl font-semibold mb-4">1. Choose Your Plan</h2>
          
          {/* Standard Plans */}
          <div className="space-y-3 mb-6">
            <p className="text-sm text-stone-500 font-medium">Standard Listings</p>
            {Object.entries(PLANS).filter(([_, p]) => !p.featured).map(([name, plan]) => (
              <button
                key={name}
                type="button"
                onClick={() => setSelectedPlan(name)}
                className={`w-full p-4 rounded-sm border-2 text-left transition-all ${
                  selectedPlan === name 
                    ? 'border-[#5C0120] bg-[#5C0120]/5' 
                    : 'border-stone-200 hover:border-stone-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{name}</span>
                      {plan.popular && (
                        <span className="text-xs bg-[#5C0120] text-white px-2 py-0.5 rounded">POPULAR</span>
                      )}
                      {plan.savings && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{plan.savings}</span>
                      )}
                    </div>
                    <p className="text-sm text-stone-500">{plan.description}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-bold">{plan.price}</span>
                    <span className="text-stone-500 text-sm">{plan.period}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          {/* Featured Plans */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Crown className="h-4 w-4 text-[#D4AF37]" />
              <p className="text-sm text-stone-500 font-medium">Featured Listings</p>
            </div>
            {Object.entries(PLANS).filter(([_, p]) => p.featured).map(([name, plan]) => (
              <button
                key={name}
                type="button"
                onClick={() => setSelectedPlan(name)}
                className={`w-full p-4 rounded-sm border-2 text-left transition-all ${
                  selectedPlan === name 
                    ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                    : 'border-[#D4AF37]/30 hover:border-[#D4AF37]/60'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-[#D4AF37] fill-current" />
                      <span className="font-semibold">{name}</span>
                      {plan.savings && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{plan.savings}</span>
                      )}
                    </div>
                    <p className="text-sm text-stone-500">{plan.description}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xl font-bold text-[#D4AF37]">{plan.price}</span>
                    <span className="text-stone-500 text-sm">{plan.period}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          {/* Plan Benefits */}
          <div className="mt-6 p-4 bg-stone-50 rounded-sm">
            <p className="text-sm font-medium mb-2">All plans include:</p>
            <ul className="space-y-1 text-sm text-stone-600">
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Business listing in directory</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Up to 10 gallery images</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Contact form for enquiries</li>
              <li className="flex items-center gap-2"><Check className="h-4 w-4 text-green-500" /> Social media links</li>
            </ul>
            <div className="mt-3 pt-3 border-t border-stone-200">
              <p className="text-sm font-medium mb-2 text-[#D4AF37]">Featured vendors also get:</p>
              <ul className="space-y-1 text-sm text-stone-600">
                <li className="flex items-center gap-2"><Star className="h-4 w-4 text-[#D4AF37]" /> Appears at top of category</li>
                <li className="flex items-center gap-2"><Star className="h-4 w-4 text-[#D4AF37]" /> Blog feature</li>
                <li className="flex items-center gap-2"><Star className="h-4 w-4 text-[#D4AF37]" /> Social media shoutout</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Right Column - Registration Form */}
        <div>
          <h2 className="text-xl font-semibold mb-4">2. Business Details</h2>
          
          <form onSubmit={handleSubmit} className="bg-white p-6 rounded-sm border border-stone-200 shadow-sm space-y-4">
            <div>
              <Label htmlFor="business-name">Business Name *</Label>
              <Input
                id="business-name"
                type="text"
                placeholder="Your Business Name"
                value={formData.business_name}
                onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                required
                data-testid="vendor-business-name"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor-reg-email">Email *</Label>
                <Input
                  id="vendor-reg-email"
                  type="email"
                  placeholder="email@business.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                  data-testid="vendor-reg-email"
                />
              </div>
              <div>
                <Label htmlFor="vendor-reg-password">Password *</Label>
                <div className="relative">
                  <Input
                    id="vendor-reg-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                    className="pr-10"
                    required
                    data-testid="vendor-reg-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-stone-500 shadow-sm hover:text-stone-700"
                    data-testid="vendor-reg-toggle-password"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor-phone">Phone *</Label>
                <Input
                  id="vendor-phone"
                  type="tel"
                  placeholder="+350 200 12345"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  data-testid="vendor-phone"
                />
              </div>
              <div>
                <Label htmlFor="vendor-address">Address *</Label>
                <Input
                  id="vendor-address"
                  type="text"
                  placeholder="Your business address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  data-testid="vendor-address"
                />
              </div>
            </div>
            
            <div className="space-y-3" data-testid="vendor-category-selection-block">
              <div>
                <Label htmlFor="vendor-primary-category">Primary Category *</Label>
                <Select value={formData.primary_category || undefined} onValueChange={setPrimaryCategory}>
                  <SelectTrigger id="vendor-primary-category" className="mt-1" data-testid="vendor-primary-category-select-trigger">
                    <SelectValue placeholder="Select primary category" />
                  </SelectTrigger>
                  <SelectContent data-testid="vendor-primary-category-select-content">
                    {availableCategories.map((category) => (
                      <SelectItem
                        key={category}
                        value={category}
                        data-testid={`vendor-primary-category-option-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Secondary Categories (optional)</Label>
                <p className="text-xs text-stone-500 mt-1" data-testid="vendor-secondary-category-help-text">
                  Secondary categories appear after primary vendors on category pages.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  {availableCategories.map((category) => {
                    const isPrimary = formData.primary_category === category;
                    const isSelected = formData.secondary_categories.includes(category);
                    return (
                      <button
                        key={category}
                        type="button"
                        onClick={() => toggleSecondaryCategory(category)}
                        disabled={isPrimary}
                        className={`w-full min-w-0 whitespace-normal break-words leading-tight text-xs py-2 px-2 rounded-sm border transition-colors ${
                          isPrimary
                            ? 'bg-stone-100 border-stone-200 text-stone-400 cursor-not-allowed'
                            : isSelected
                              ? 'bg-[#D4AF37] border-[#D4AF37] text-white'
                              : 'bg-white border-stone-200 text-stone-600 hover:border-[#D4AF37]'
                        }`}
                        data-testid={`vendor-secondary-category-option-${category.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      >
                        {category}
                        {isPrimary ? ' (Primary)' : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            
            <div>
              <Label htmlFor="vendor-description">Business Description *</Label>
              <textarea
                id="vendor-description"
                placeholder="Tell us about your business..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                className="w-full min-h-[80px] p-3 border border-stone-200 rounded-sm text-sm resize-none focus:outline-none focus:border-[#D4AF37]"
                data-testid="vendor-description"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label htmlFor="vendor-website">Website</Label>
                <Input
                  id="vendor-website"
                    type="text"
                  placeholder="https://"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                />
              </div>
              <div>
                <Label htmlFor="vendor-instagram">Instagram</Label>
                <Input
                  id="vendor-instagram"
                  type="text"
                  placeholder="@handle"
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="vendor-facebook">Facebook</Label>
                <Input
                  id="vendor-facebook"
                  type="text"
                  placeholder="Page name"
                  value={formData.facebook}
                  onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor-payment-method">How would you like to pay?</Label>
                <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                  <SelectTrigger id="vendor-payment-method" className="input-wedding" data-testid="vendor-payment-method-select">
                    <SelectValue placeholder="Select payment method (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="By Revolut">By Revolut</SelectItem>
                    <SelectItem value="By Card">By Card</SelectItem>
                  </SelectContent>
                </Select>
                {formData.payment_method === 'By Card' && (
                  <p className="mt-2 text-xs text-stone-500">We will contact you to arrange card payment.</p>
                )}
              </div>
              <div>
                <Label htmlFor="vendor-invoice-name">Invoice made out to</Label>
                <Input
                  id="vendor-invoice-name"
                  type="text"
                  placeholder="Business or contact name"
                  value={formData.invoice_name}
                  onChange={(e) => setFormData({ ...formData, invoice_name: e.target.value })}
                  data-testid="vendor-invoice-name-input"
                />
              </div>
            </div>

            <div className="rounded-sm border border-[#D4AF37]/25 bg-[#fffaf0] p-3 text-sm text-stone-600" data-testid="vendor-payment-info-note">
              No payment is taken in your dashboard at this stage. We will contact you using your selected payment method.
            </div>
            
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
            
            <Button
              type="submit"
              className="w-full bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white"
              disabled={loading || !selectedPlan}
              data-testid="vendor-register-btn"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Submit Application
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            
            <p className="text-xs text-stone-500 text-center">
              By submitting, you agree to our terms. We will review your application within 2-3 business days.
            </p>
            
            <p className="text-center text-sm text-stone-500 pt-2 border-t">
              Already have an account?{' '}
              <button type="button" onClick={onSwitchToLogin} className="text-[#D4AF37] font-medium hover:underline">
                Sign in
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
